/**
 * @fileoverview Modal dialog for editing bookmark item properties.
 * @module ui/BookmarkDialog
 */

import { BaseDialog } from './BaseDialog.js';
import { escapeHtml, normalizeCssColorToHex } from '../utils/jrfuncs.js';
import { formatAddressForItemNicely } from '../utils/nynfuncs.js';
import { MAP_CUSTOMS, DOM_ELEMENTS, USER_ICON_PICKER_IMAGELIST } from '../constants.js';

const { CLASS_ICON_PICKER_ITEM } = DOM_ELEMENTS;


/**
 * Modal dialog for editing bookmark item properties.
 * Allows users to add/edit notes and view bookmark details.
 * @class
 * @extends BaseDialog
 */
export class BookmarkDialog extends BaseDialog {
    /**
     * Creates a new BookmarkDialog instance.
     * @param {Object} options - Configuration options
     * @param {Object} options.mapApp - The MapApp instance (for ImageHelper access)
     * @param {Object} options.item - The bookmark item to edit
     * @param {Array<string>} [options.imageListKeys] - Image list keys for pin icons
     */
    constructor(options = {}) {
        super({ idPrefix: 'bookmarkDialog' });

        this.mapApp = options.mapApp;
        this.imageListKeys = options.imageListKeys || USER_ICON_PICKER_IMAGELIST;
        this.item = options.item;
        this.options = {
            title: 'Edit Bookmark',
            saveButtonText: 'Save and close',
            cancelButtonText: 'Cancel'
        };

        this.lastAction = null;
    }

    /**
     * Called before the modal is shown.
     * Populates form fields with current item values.
     * @override
     */
    onBeforeShow() {
        const labelInput = this.getElement('_label');
        if (labelInput && this.item.userLabel) {
            labelInput.value = this.item.userLabel;
        }

        const notesInput = this.getElement('_notes');
        if (notesInput && this.item.userNotes) {
            notesInput.value = this.item.userNotes;
        }

        const customPinToggle = this.getElement('_customPin');
        if (customPinToggle) {
            customPinToggle.checked = this.item.userLook ?? false;
        }

        const calloutToggle = this.getElement('_showCallout');
        if (calloutToggle) {
            calloutToggle.checked = this.item.userCallout ?? false;
        }

        const pinSelect = this.getElement('_pin');
        if (pinSelect && this.item.userPin) {
            pinSelect.value = this.item.userPin;
        }

        const colorInput = this.getElement('_color');
        const colorHexInput = this.getElement('_colorHex');
        const colorValue = normalizeCssColorToHex(this.item.userColor || MAP_CUSTOMS.COLOR_DEFAULT);
        if (colorInput) {
            colorInput.value = colorValue;
        }
        if (colorHexInput) {
            colorHexInput.value = colorValue;
        }

        const scaleInput = this.getElement('_scale');
        const scaleValueSpan = this.getElement('_scaleValue');
        const scaleValue = this.item.userScale || 100;
        if (scaleInput) {
            scaleInput.value = scaleValue;
        }
        if (scaleValueSpan) {
            scaleValueSpan.textContent = `${scaleValue}%`;
        }

        // Set initial state of appearance controls based on customPin toggle
        this._setAppearanceControlsEnabled(this.item.userLook ?? false);

        // update pin preview without scrolling (scroll deferred to onAfterShow)
        //this._updatePinPreview(false);
    }

    /**
     * Called after modal is fully shown. Scrolls to the selected icon in the picker.
     * @override
     */
    onAfterShow() {
        this._updatePinPreview(true);
    }

    /**
     * Returns the HTML for the bookmark edit modal.
     * Uses buildModalHtml template from BaseDialog.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        const item = this.item;

        // Build item details display
        const detailsHtml = this._buildItemDetailsHtml(item);

        // user-editable (and importable) values interpolated into attributes below
        const imageHelper = this.mapApp.getImageHelper();
        // (the colour input deliberately has no `alpha` attribute: with it Safari 18.4+ reports
        // color(srgb ...) values, and nothing in the map uses transparency; a value Safari already
        // stored is repaired here and on save by normalizeCssColorToHex)
        const colorValue = normalizeCssColorToHex(item.userColor || MAP_CUSTOMS.COLOR_DEFAULT);
        const colorAttr = escapeHtml(colorValue);
        const scaleAttr = escapeHtml(item.userScale || 100);
        const pinImageSrcAttr = escapeHtml(imageHelper.findImageSrcInImageLists(this.imageListKeys, item.userPin));

        const bodyHtml = `
            <div class="bookmark-dialog-content">
                <div class="bookmark-dialog-details mb-3">
                    <label class="form-label fw-bold">Bookmark Details:</label>
                    <div class="bookmark-dialog-item-info border rounded p-2 bg-light">
                        ${detailsHtml}
                    </div>
                </div>
                <div class="bookmark-dialog-label mb-3 d-flex align-items-center gap-2">
                    <label for="${this.modalId}_label" class="form-label fw-bold mb-0">Label:</label>
                    <input
                        type="text"
                        class="form-control flex-grow-1"
                        id="${this.modalId}_label"
                        placeholder="Custom label for this bookmark..."
                        value="${escapeHtml(item.userLabel || '', false)}"
                    >
                </div>
                <div class="bookmark-dialog-appearance-group">
                    <div class="bookmark-dialog-custompin mb-2 d-flex align-items-center gap-2">
                        <label class="form-label fw-bold mb-0">Customize Appearance:</label>
                        <div class="form-check form-switch mb-0">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="${this.modalId}_customPin"
                                ${item.userLook ? 'checked' : ''}
                            >
                        </div>
                    </div>
                    <div class="bookmark-dialog-appearance-controls" id="${this.modalId}_appearanceControls">
                        <div class="bookmark-dialog-callout mb-3 d-flex align-items-center gap-2">
                    <label class="form-label fw-bold mb-0">Show callout:</label>
                    <div class="form-check form-switch mb-0">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="${this.modalId}_showCallout"
                            ${item.userCallout ? 'checked' : ''}
                        >
                    </div>
                </div>
                <div class="bookmark-dialog-pin mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label fw-bold mb-0">Icon:</label>
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            id="${this.modalId}_pinSearch"
                            placeholder="Search icons..."
                            style="width: 150px;"
                        >
                        <div
                            id="${this.modalId}_pinPreview"
                            class="icon-picker-preview"
                            style="
                                width: 24px;
                                height: 24px;
                                background-color: ${colorAttr};
                                -webkit-mask-image: url('${pinImageSrcAttr}');
                                mask-image: url('${pinImageSrcAttr}');
                                -webkit-mask-size: contain;
                                mask-size: contain;
                                -webkit-mask-repeat: no-repeat;
                                mask-repeat: no-repeat;
                                -webkit-mask-position: center;
                                mask-position: center;
                            "
                        ></div>
                        <span id="${this.modalId}_pinCaption" class="text-muted small">${escapeHtml(imageHelper.findCaptionInImageLists(this.imageListKeys, item.userPin))}</span>
                    </div>
                    <input type="hidden" id="${this.modalId}_pin" value="${escapeHtml(item.userPin || '', false)}">
                    <div class="icon-picker-grid-container" id="${this.modalId}_pinGrid">
                        ${imageHelper.renderImageListsAsIconGridHtml(USER_ICON_PICKER_IMAGELIST, item.userPin, colorValue)}
                    </div>
                </div>
                <div class="bookmark-dialog-color mb-3 d-flex align-items-center gap-2 flex-wrap">
                    <label for="${this.modalId}_color" class="form-label fw-bold mb-0">Color:</label>
                    <input
                        type="color"
                        class="form-control form-control-color"
                        id="${this.modalId}_color"
                        value="${colorAttr}"
                    >
                    <input
                        type="text"
                        class="form-control"
                        id="${this.modalId}_colorHex"
                        value="${colorAttr}"
                        style="width: 90px; font-family: monospace;"
                        maxlength="7"
                        placeholder="#000000"
                    >
                    <label for="${this.modalId}_scale" class="form-label fw-bold mb-0 ms-2">Size:</label>
                    <input
                        type="range"
                        class="form-range"
                        id="${this.modalId}_scale"
                        min="${MAP_CUSTOMS.PIN_ICON_SCALE.MIN}"
                        max="${MAP_CUSTOMS.PIN_ICON_SCALE.MAX}"
                        value="${scaleAttr}"
                        style="width: 100px;"
                    >
                    <span id="${this.modalId}_scaleValue" class="text-muted small" style="min-width: 45px;">${scaleAttr}%</span>
                        </div>
                    </div>
                </div>
                <div class="bookmark-dialog-notes">
                    <label for="${this.modalId}_notes" class="form-label fw-bold">Notes:</label>
                    <textarea
                        class="form-control"
                        id="${this.modalId}_notes"
                        rows="4"
                        placeholder="Add your notes about this bookmark..."
                    >${escapeHtml(item.userNotes || '', false)}</textarea>
                </div>
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                ${this.options.cancelButtonText}
            </button>
            <button type="button" class="btn btn-primary" id="${this.modalId}_saveBtn">
                <i class="bi bi-check-lg"></i> ${this.options.saveButtonText}
            </button>
        `;

        return this.buildModalHtml({
            title: this.options.title,
            icon: 'bi-bookmark-star',
            bodyHtml,
            footerHtml,
            size: 'modal-md'
        });
    }

    /**
     * Builds HTML to display item details.
     * @param {Object} item - The bookmark item
     * @returns {string} HTML string for item details
     * @private
     */
    _buildItemDetailsHtml(item) {
        const parts = [];

        // Name/label
        if (item.dName) {
            parts.push(`<div><span class="text-muted">Name:</span> ${escapeHtml(item.dName, false)}</div>`);
        } else if (item.label) {
            parts.push(`<div><span class="text-muted">Label:</span> ${escapeHtml(item.label, false)}</div>`);
        }

        // Address
        if (true) {
            let addressText = formatAddressForItemNicely(item);
            parts.push(`<div><span class="text-muted">Address:</span> ${escapeHtml(addressText, false)}</div>`);
        }
        /*
        if (item.jregionloc) {
            let jregionloc = item.jregionloc;
            parts.push(`<div><span class="text-muted">Block:</span> ${escapeHtml(jregionloc, false)}</div>`);
        }
        */

        /*
        // Type info
        if (item.ptype) {
            parts.push(`<div><span class="text-muted">Type:</span> ${escapeHtml(item.ptype, false)}</div>`);
        }

        // Category (for places)
        if (item.pcatg) {
            parts.push(`<div><span class="text-muted">Category:</span> ${escapeHtml(item.pcatg, false)}</div>`);
        }

        // UID
        if (item.uid) {
            parts.push(`<div class="small text-muted mt-1">ID: ${escapeHtml(item.uid, false)}</div>`);
        }
        */

        return parts.join('') || '<div class="text-muted">No details available</div>';
    }

    /**
     * Updates the pin preview with current icon and color.
     * @private
     */
    _updatePinPreview(flagGotoIcon) {
        const pinPreview = this.getElement('_pinPreview');
        const pinInput = this.getElement('_pin');
        const pinCaption = this.getElement('_pinCaption');
        const colorInput = this.getElement('_color');

        let pinPathVal = pinInput.value;
        if (!pinPathVal) {
            // get it based on the item pcatg icon?
            pinPathVal = this.mapApp.getImageHelper().calcUserImagePathForItemRelative(this.item);
        }

        if (pinPreview && pinPathVal && colorInput) {
            const imageHelper = this.mapApp.getImageHelper();
            const imageUrl = imageHelper.findImageSrcInImageLists(this.imageListKeys, pinPathVal);
            pinPreview.style.maskImage = `url('${imageUrl}')`;
            pinPreview.style.webkitMaskImage = `url('${imageUrl}')`;
            pinPreview.style.backgroundColor = colorInput.value;

            if (pinCaption) {
                pinCaption.textContent = imageHelper.findCaptionInImageLists(this.imageListKeys, pinPathVal);
            }

        if (flagGotoIcon) {
            // go to the icon in the chooser; defer scroll until layout is ready (modal may still be animating)
            const pinGrid = this.getElement('_pinGrid');
            if (pinGrid && pinPathVal) {
                const items = pinGrid.querySelectorAll('.' + CLASS_ICON_PICKER_ITEM);
                for (const item of items) {
                    if (item.dataset.value === pinPathVal) {
                        item.classList.add('selected');
                        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        break;
                    }
                }
            }
        }
        }
    }

    /**
     * Updates all icon grid items with the current color.
     * @private
     */
    _updateIconGridColors() {
        const colorInput = this.getElement('_color');
        const pinGrid = this.getElement('_pinGrid');

        if (colorInput && pinGrid) {
            const items = pinGrid.querySelectorAll('.' + CLASS_ICON_PICKER_ITEM);
            items.forEach(item => {
                item.style.backgroundColor = colorInput.value;
            });
        }
    }

    /**
     * Filters the icon grid based on search text.
     * @param {string} searchText - Text to filter by
     * @private
     */
    _filterIconGrid(searchText) {
        const pinGrid = this.getElement('_pinGrid');
        if (!pinGrid) return;

        const items = pinGrid.querySelectorAll('.' + CLASS_ICON_PICKER_ITEM);
        const lowerSearch = searchText.toLowerCase();

        items.forEach(item => {
            const caption = item.dataset.caption.toLowerCase();
            const matches = caption.includes(lowerSearch);
            item.style.display = matches ? '' : 'none';
        });
    }

    /**
     * Selects an icon in the grid.
     * @param {HTMLElement} iconButton - The clicked icon button
     * @private
     */
    _selectIcon(iconButton) {
        const pinGrid = this.getElement('_pinGrid');
        const pinInput = this.getElement('_pin');

        if (!pinGrid || !pinInput) return;

        // Remove selection from all items
        pinGrid.querySelectorAll('.' + CLASS_ICON_PICKER_ITEM + '.selected').forEach(item => {
            item.classList.remove('selected');
        });

        // Select the clicked item
        iconButton.classList.add('selected');
        pinInput.value = iconButton.dataset.value;

        this._updatePinPreview(false);
    }

    /**
     * Enables or disables appearance controls based on customPin toggle state.
     * @param {boolean} enabled - Whether appearance controls should be enabled
     * @private
     */
    _setAppearanceControlsEnabled(enabled) {
        const appearanceControls = this.getElement('_appearanceControls');
        const calloutToggle = this.getElement('_showCallout');
        const pinSearch = this.getElement('_pinSearch');
        const pinGrid = this.getElement('_pinGrid');
        const colorInput = this.getElement('_color');
        const colorHexInput = this.getElement('_colorHex');
        const scaleInput = this.getElement('_scale');

        // Toggle disabled class on the container for visual styling
        if (appearanceControls) {
            appearanceControls.classList.toggle('disabled', !enabled);
        }

        if (calloutToggle) calloutToggle.disabled = !enabled;
        if (pinSearch) pinSearch.disabled = !enabled;
        if (colorInput) colorInput.disabled = !enabled;
        if (colorHexInput) colorHexInput.disabled = !enabled;
        if (scaleInput) scaleInput.disabled = !enabled;

        // Add/remove disabled class on grid container for styling
        if (pinGrid) {
            pinGrid.classList.toggle('disabled', !enabled);
        }
    }

    /**
     * Sets up event listeners for modal interactions.
     * @override
     */
    setupEventListeners() {
        const saveBtn = this.getElement('_saveBtn');

        // Save button click
        saveBtn.addEventListener('click', () => {
            this.lastAction = 'save';
            this.modal.hide();
        });

        // Customize Appearance toggle - enable/disable related controls
        const customPinToggle = this.getElement('_customPin');
        if (customPinToggle) {
            customPinToggle.addEventListener('change', () => {
                this._setAppearanceControlsEnabled(customPinToggle.checked);
            });
        }

        // Sync color picker and hex input bidirectionally
        const colorInput = this.getElement('_color');
        const colorHexInput = this.getElement('_colorHex');

        if (colorInput && colorHexInput) {
            colorInput.addEventListener('input', () => {
                colorHexInput.value = normalizeCssColorToHex(colorInput.value);
                this._updatePinPreview(false);
                this._updateIconGridColors();
            });

            colorHexInput.addEventListener('input', () => {
                const hex = colorHexInput.value;
                if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
                    colorInput.value = hex;
                    this._updatePinPreview(false);
                    this._updateIconGridColors();
                }
            });
        }

        // Icon grid click handler (event delegation)
        const pinGrid = this.getElement('_pinGrid');
        if (pinGrid) {
            pinGrid.addEventListener('click', (e) => {
                const iconButton = e.target.closest('.' + CLASS_ICON_PICKER_ITEM);
                if (iconButton) {
                    this._selectIcon(iconButton);
                }
            });
        }

        // Icon search/filter handler
        const pinSearch = this.getElement('_pinSearch');
        if (pinSearch) {
            pinSearch.addEventListener('input', () => {
                this._filterIconGrid(pinSearch.value);
            });
        }

        // Scale slider handler
        const scaleInput = this.getElement('_scale');
        const scaleValueSpan = this.getElement('_scaleValue');
        if (scaleInput && scaleValueSpan) {
            scaleInput.addEventListener('input', () => {
                scaleValueSpan.textContent = `${scaleInput.value}%`;
            });
        }
    }


    /**
     * Returns the dialog result based on the last user action.
     * If the user saved, returns form values; otherwise returns a cancel action.
     * @returns {{action: string, userLabel?: string, userNotes?: string, userLook?: boolean, userCallout?: boolean, userPin?: string, userColor?: string, userScale?: number}} Result object with action and optional form values
     * @override
     */
    getDefaultResult() {
        if (this.lastAction === 'save') {
            const labelInput = this.getElement('_label');
            const notesInput = this.getElement('_notes');
            const customPinToggle = this.getElement('_customPin');
            const calloutToggle = this.getElement('_showCallout');
            const pinSelect = this.getElement('_pin');
            const colorInput = this.getElement('_color');
            const scaleInput = this.getElement('_scale');

            return {
                action: 'save',
                userLabel: labelInput ? labelInput.value.trim() : '',
                userNotes: notesInput ? notesInput.value.trim() : '',
                userLook: customPinToggle ? customPinToggle.checked : false,
                userCallout: calloutToggle ? calloutToggle.checked : false,
                userPin: pinSelect ? pinSelect.value : 'default',
                userColor: colorInput ? normalizeCssColorToHex(colorInput.value) : MAP_CUSTOMS.COLOR_DEFAULT,
                userScale: scaleInput ? parseInt(scaleInput.value, 10) : 100
            };
        }
        return { action: 'cancel' };
    }

    /**
     * Shows the bookmark edit dialog (singleton - only one can be open at a time).
     * @param {Object} options - Configuration options
     * @param {Object} options.mapApp - The MapApp instance
     * @param {Object} options.item - The bookmark item to edit
     * @returns {Promise<{action: string, ...}>} Result with action and form values
     */
    static async showBookmarkDialog(options = {}) {
        return BaseDialog.showSingleton(BookmarkDialog, options);
    }
}
